<script>
  import { browser } from '$app/environment';

  import moment from 'moment';
  import Auth from '@utils/auth';
  import ErrorService from '@utils/error-service';
  import Nudge from '@utils/nudge';
  import { getStores } from '$app/stores';

  import { beforeNavigate, afterNavigate } from '$app/navigation';

  import '@vendor/bootstrap.css';
  import 'nprogress/nprogress.css';
  import 'tippy.js/dist/tippy.css';
  import '@vendor/app.scss';

  import FbqEvents from '@ajax/fbq_events';

  import { setContext, onMount } from 'svelte';
  import { initializeGlobalStores, getGlobalStore } from '@stores/global';
  import { initializeCurrentUserStores, getCurrentUserStore, cleanUserData } from '@stores/current_user';

  import Cookie from 'js-cookie';
  import store2 from 'store2';
  import Mixpanel from '@utils/mixpanel';
  import * as Sentry from '@sentry/browser';
  import currentUserSession from '@utils/currentUserSession';
  import { writable } from 'svelte/store';

  import NProgress from 'nprogress';
  import GroupPreload from '@app-components/preload/Group.svelte';
  import BlankPreloader from '@app-components/preload/Blank.svelte';
  import ReportContentModal from '@app-components/modal/ReportContentModal.svelte';

  export let data;
  let { deviceType, benableUser, currentUser: currentUserContext, reportedContent, blockedUsers } = data;
  $: ({ deviceType, benableUser, currentUser: currentUserContext, reportedContent, blockedUsers } = data);

  setContext('deviceType', deviceType);
  setContext('benableUser', benableUser);
  setContext('reportedContent', writable(reportedContent));
  setContext('blockedUsers', writable(blockedUsers));

  initializeGlobalStores();
  initializeCurrentUserStores({ default: cleanUserData(currentUserContext) });

  let currentUser = getCurrentUserStore('default');
  let reportContentStore = getGlobalStore('reportContentStore');
  let preloading = false;
  let preloadGroup = false;

  if (browser) {
    let destroyInt;

    let destroy = () => {
      NProgress.done();
      preloading = false;
      clearInterval(destroyInt);
    };

    afterNavigate(() => destroy());

    beforeNavigate((nav) => {
      NProgress.start();

      preloading = true;

      if (nav && nav.type == 'popstate') {
        return;
      }

      let route = nav && nav.to && nav.to.route;
      let currentUrl = location.href;

      clearInterval(destroyInt);

      destroyInt = setInterval(() => {
        if (location.href != currentUrl) {
          destroy();
        }
      }, 100);

      preloadGroup = route && route.id == '/(app)/[username_or_id]/[slug]';
    });

    ErrorService.init();
    // after a deploy, dynamic import may fail. We just need to trigger a full reload.
    window.addEventListener('unhandledrejection', function (e) {
      if (window.APP_ENV === 'production' && ErrorService.isPageLoadException(e)) {
        console.warn('[fetch dynamically imported failed] Refreshing page');
        location.reload(true);
        e.preventDefault();
      }
    });

    let runOnce = async function (key, fn) {
      if (window.runOnce === undefined) {
        window.runOnce = {};
      }

      if (window.runOnce[key] !== true) {
        await fn();
        window.runOnce[key] = true;
      }
    };

    let trackPageEvent = async function (data) {
      let to = data && data.to;
      let from = data && data.from;

      if (to) {
        const Track = (await import('../../utils/track')).default;
        var to_path = null;
        var from_path = null;
        if (to && to.url) {
          to_path = to.url.pathname + to.url.search;
        } else {
          if (to && to.pathname) {
            to_path = to.pathname + to.search;
          }
        }

        if (from && from.url) {
          from_path = from.url.pathname + from.url.search;
        } else {
          if (from && from.pathname) {
            from_path = from.pathname + from.search;
          }
        }

        Track.pageView({
          user: currentUser,
          from_path: from_path,
          to_path: to_path,
        });
      }
    };

    let setupNudge = function () {
      try {
        if (
          typeof window !== 'undefined' &&
          currentUser &&
          currentUser.id &&
          (window.lastNudgeRetrieval === undefined || new Date() - window.lastNudgeRetrieval > 30 * 1000)
        ) {
          Nudge.setup();
          window.lastNudgeRetrieval = new Date();
        }
      } catch {}
    };

    runOnce('navigation-store', async () => {
      trackPageEvent({
        to: { pathname: window.location.pathname, search: window.location.search },
        from: { pathname: document.referrer, search: '' },
      });

      getStores().navigating.subscribe((x) => {
        if (x) {
          window.pageNavigation = x;
          trackPageEvent(x);
          setupNudge();
        }
      });
    });

    let setupFS = function () {
      if (window.APP_ENV === 'production') {
        runOnce('setup-fs', async () => {
          try {
            const FullStory = (await import('@utils/fullstory')).default;
            FullStory.setup(currentUser);
          } catch (ex) {
            console.log(`Setting up fullstory failed | ${ex}`);
          }
        });
      }
    };

    let setupVisitorCountry = async function () {
      runOnce('setup-visitor-country', async () => {
        try {
          const Geoip = (await import('@utils/geoip')).default;
          Geoip.setup();
        } catch (ex) {
          console.log(`Setting up geoip failed | ${ex}`);
        }
      });
    };

    let setupIntercom = function () {
      // only activate on tablet / desktop
      if (window.APP_ENV === 'production' && window.innerWidth >= 880) {
        var timesRan = 0;
        var countryCode;
        let eligibleForSetUp = false;
        let randomNumber = Math.floor(Math.random() * 10) + 1;
        const coreCountryCodes = ['US', 'CA', 'NZ', 'AU', 'NO', 'GB', 'IS', 'CH', 'DE', 'IE', 'ES', 'IT', 'NL', 'MX'];

        let interval = setInterval(() => {
          timesRan += 1;
          countryCode = Cookie.get('benable-country-code');
          if (countryCode) {
            eligibleForSetUp =
              coreCountryCodes.includes(countryCode) ||
              randomNumber === 1 ||
              window.location.href.includes('list-optimization-tips');
          }

          if (typeof window.Intercom === 'function' && window.INTERCOM_APP_ID && eligibleForSetUp) {
            let data = { app_id: window.INTERCOM_APP_ID };

            if (currentUser && currentUser.name && currentUser.name != '') {
              data['name'] = currentUser.name;
            }

            if (currentUser && currentUser.email && currentUser.email != '') {
              data['email'] = currentUser.email;
            }

            window.Intercom('boot', data);
            clearInterval(interval);
          }

          if (timesRan > 10 || countryCode) {
            clearInterval(interval);
          }
        }, 500);
      }
    };

    let clearWelcomeNoteCookies = function () {
      try {
        let cookies = Cookie.get();

        for (let key in cookies) {
          if (key.includes('welcome-note')) {
            Cookie.remove(key);
          }
        }
      } catch {}
    };

    let setBenableCookie = async function (user) {
      if (user && user.benable_user) {
        const Cookie = (await import('js-cookie')).default;

        if (Cookie.get('benable-user')) {
          return;
        }

        Cookie.set('benable-user', true, { expires: 365 });
      }
    };

    let markFbqEventsAsProcessed = async function () {
      if (typeof window === 'undefined' || typeof window.fbq === 'undefined' || !currentUser || !currentUser.id) {
        return;
      }

      let fbqEvents = await FbqEvents.index();

      if (!fbqEvents || fbqEvents.length <= 0) {
        return;
      }

      fbqEvents.forEach((x) => {
        window.fbq('track', x.event_name);
      });

      FbqEvents.markAsProcesed(fbqEvents.map((x) => x.id));
    };

    onMount(() => {
      currentUser.set(currentUserSession());

      currentUser.subscribe((data) => {
        if (data.id || data.name) {
          let cleanedData = cleanUserData(data);
          store2('current_user', cleanedData);

          Cookie.set(
            'current_user',
            JSON.stringify({
              ...cleanedData,
              following_ids: [],
              follower_ids: [],
              bio: '',
              social_links: [],
            }),
            {
              expires: 360,
            }
          );

          Sentry.setUser({ email: data.email });

          Mixpanel.identify(data.id);
          Mixpanel.peopleSet({ id: data.id, email: data.email, name: data.name });
        } else {
          Cookie.set('current_user', null);
          Cookie.set('current_user_datetime', null);
          Cookie.set('current_user_last_shown_notification', null);
          store2('current_user', null);
          Mixpanel.reset();
        }
      });

      runOnce('refetch-user', () => {
        // fetch a new copy on of user
        if (currentUser && currentUser.api_token) {
          Auth.load(currentUser, currentUser.api_token);
        }

        currentUser.subscribe((resp) => {
          currentUser = resp;
          setBenableCookie(resp);
        });
      });

      runOnce('setup-user-realtime', async () => {
        if (currentUser && currentUser.id) {
          const realtime = (await import('../../utils/realtime')).default;
          realtime.subscribe(currentUser, 'action:refresh', () => location.reload());
        }
      });

      runOnce('track-fbq-events', async () => {
        try {
          markFbqEventsAsProcessed();
        } catch {}
      });

      setupFS();
      setupIntercom();
      setupVisitorCountry();
      clearWelcomeNoteCookies();
      setupNudge();

      let unsubreportContentStore = reportContentStore.subscribe((x) => {
        reportContentStore = x;
      });

      window.onMountLoaded = true;

      return () => {
        unsubreportContentStore;
      };
    });
  }
</script>

<svelte:head>
  {#if browser}
    <script>
      ((g) => {
        var h,
          a,
          k,
          p = 'The Google Maps JavaScript API',
          c = 'google',
          l = 'importLibrary',
          q = '__ib__',
          m = document,
          b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}),
          r = new Set(),
          e = new URLSearchParams(),
          u = () =>
            h ||
            (h = new Promise(async (f, n) => {
              await (a = m.createElement('script'));
              e.set('libraries', [...r] + '');
              for (k in g)
                e.set(
                  k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()),
                  g[k]
                );
              e.set('callback', c + '.maps.' + q);
              a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
              d[q] = f;
              a.onerror = () => (h = n(Error(p + ' could not load.')));
              a.nonce = m.querySelector('script[nonce]')?.nonce || '';
              m.head.append(a);
            }));
        d[l]
          ? console.warn(p + ' only loads once. Ignoring:', g)
          : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
      })({
        key: window.GOOGLE_PLACES_API_KEY,
        v: 'weekly',
      });
    </script>

    {#if window.APP_ENV === 'production'}
      <script async src="https://www.googletagmanager.com/gtag/js?id=UA-164073041-1"></script>
      <script>
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', 'UA-164073041-1');
      </script>

      <!-- Meta Pixel Code -->
      <script>
        !(function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '545609384970898');
        fbq('track', 'PageView');
      </script>
      <noscript>
        <img height="1" width="1" src="https://www.facebook.com/tr?id=545609384970898&ev=PageView&noscript=1" />
      </noscript>
      <!-- End Meta Pixel Code -->

      {#if !moment().local().toString().endsWith('GMT+0530')}
        <script>
          !(function (w, d, t) {
            w.TiktokAnalyticsObject = t;
            var ttq = (w[t] = w[t] || []);
            (ttq.methods = [
              'page',
              'track',
              'identify',
              'instances',
              'debug',
              'on',
              'off',
              'once',
              'ready',
              'alias',
              'group',
              'enableCookie',
              'disableCookie',
            ]),
              (ttq.setAndDefer = function (t, e) {
                t[e] = function () {
                  t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                };
              });
            for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
            (ttq.instance = function (t) {
              for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
              return e;
            }),
              (ttq.load = function (e, n) {
                var i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
                (ttq._i = ttq._i || {}),
                  (ttq._i[e] = []),
                  (ttq._i[e]._u = i),
                  (ttq._t = ttq._t || {}),
                  (ttq._t[e] = +new Date()),
                  (ttq._o = ttq._o || {}),
                  (ttq._o[e] = n || {});
                var o = document.createElement('script');
                (o.type = 'text/javascript'), (o.async = !0), (o.src = i + '?sdkid=' + e + '&lib=' + t);
                var a = document.getElementsByTagName('script')[0];
                a.parentNode.insertBefore(o, a);
              });

            ttq.load('CASRDFJC77UBO7QI5U00');
            ttq.page();
          })(window, document, 'ttq');
        </script>
      {/if}

      <script>
        window.INTERCOM_APP_ID = 'tydzl04c';

        (function () {
          var w = window;
          var ic = w.Intercom;
          if (typeof ic === 'function') {
            ic('reattach_activator');
            ic('update', w.intercomSettings);
          } else {
            var d = document;
            var i = function () {
              i.c(arguments);
            };
            i.q = [];
            i.c = function (args) {
              i.q.push(args);
            };
            w.Intercom = i;
            var l = function () {
              var s = d.createElement('script');
              s.type = 'text/javascript';
              s.async = true;
              s.src = 'https://widget.intercom.io/widget/' + INTERCOM_APP_ID;
              var x = d.getElementsByTagName('script')[0];
              x.parentNode.insertBefore(s, x);
            };
            if (document.readyState === 'complete') {
              l();
            } else if (w.attachEvent) {
              w.attachEvent('onload', l);
            } else {
              w.addEventListener('load', l, false);
            }
          }
        })();
      </script>

      <script type="text/javascript">
        (function (f, b) {
          if (!b.__SV) {
            var e, g, i, h;
            window.mixpanel = b;
            b._i = [];
            b.init = function (e, f, c) {
              function g(a, d) {
                var b = d.split('.');
                2 == b.length && ((a = a[b[0]]), (d = b[1]));
                a[d] = function () {
                  a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
                };
              }
              var a = b;
              'undefined' !== typeof c ? (a = b[c] = []) : (c = 'mixpanel');
              a.people = a.people || [];
              a.toString = function (a) {
                var d = 'mixpanel';
                'mixpanel' !== c && (d += '.' + c);
                a || (d += ' (stub)');
                return d;
              };
              a.people.toString = function () {
                return a.toString(1) + '.people (stub)';
              };
              i =
                'disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove'.split(
                  ' '
                );
              for (h = 0; h < i.length; h++) g(a, i[h]);
              var j = 'set set_once union unset remove delete'.split(' ');
              a.get_group = function () {
                function b(c) {
                  d[c] = function () {
                    call2_args = arguments;
                    call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
                    a.push([e, call2]);
                  };
                }
                for (
                  var d = {}, e = ['get_group'].concat(Array.prototype.slice.call(arguments, 0)), c = 0;
                  c < j.length;
                  c++
                )
                  b(j[c]);
                return d;
              };
              b._i.push([e, f, c]);
            };
            b.__SV = 1.2;
            e = f.createElement('script');
            e.type = 'text/javascript';
            e.async = !0;
            e.src =
              'undefined' !== typeof MIXPANEL_CUSTOM_LIB_URL
                ? MIXPANEL_CUSTOM_LIB_URL
                : 'file:' === f.location.protocol && '//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js'.match(/^\/\//)
                  ? 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js'
                  : '//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
            g = f.getElementsByTagName('script')[0];
            g.parentNode.insertBefore(e, g);
          }
        })(document, window.mixpanel || []);

        mixpanel.init('424aac8a86dab8d87c8807c980f40f27', {
          debug: false,
          track_pageview: true,
          persistence: 'localStorage',
        });
      </script>

      <script
        id="cookieyes"
        type="text/javascript"
        src="https://cdn-cookieyes.com/client_data/f8b7ad1cc2cbdfa0643a6ca1/script.js">
      </script>
    {/if}
  {/if}
</svelte:head>

{#if reportContentStore && reportContentStore.show}
  <ReportContentModal
    bind:showModal={reportContentStore.show}
    startStep={reportContentStore.startStep}
    params={reportContentStore.params}
    owner={reportContentStore.owner} />
{/if}

{#if preloading}
  {#if preloadGroup}
    <GroupPreload />
  {:else}
    <BlankPreloader />
  {/if}
{/if}

<slot />
